/**
 * BiomeManager - Manages biome switching based on total miles traveled
 *
 * The biome system controls the visual environment of the game.
 * As the player travels, different biomes are loaded based on mileage thresholds.
 *
 * Biomes are now data-driven via BiomeRegistry:
 * 1. Add biome config to src/data/biomes/{biome}.json
 * 2. Create biome class in src/world/biomes/ (handles colors/rendering)
 * 3. Register in biomeClassMap below
 *
 * @see src/data/biomes/ for biome configurations
 * @see src/world/biomes/BiomeConfig.js for configuration options
 */
import * as THREE from 'three';
import { biomeRegistry } from '../registries/BiomeRegistry.js';
import { BiomeRenderer } from './BiomeRenderer.js';
import { DesertBiome, DESERT_BIOME_ENTRY, DESERT_COLORS } from './biomes/DesertBiome.js';
import { MountainBiome, MOUNTAIN_BIOME_ENTRY, MOUNTAIN_COLORS } from './biomes/MountainBiome.js';
import { ForestBiome, FOREST_BIOME_ENTRY, FOREST_COLORS } from './biomes/ForestBiome.js';
import { PrairieBiome, PRAIRIE_BIOME_ENTRY, PRAIRIE_COLORS } from './biomes/PrairieBiome.js';
import { CoastalBiome, COASTAL_BIOME_ENTRY, COASTAL_COLORS } from './biomes/CoastalBiome.js';

// Import biome JSON data
import desertBiomeData from '../data/biomes/desert.json';
import mountainBiomeData from '../data/biomes/mountain.json';
import forestBiomeData from '../data/biomes/forest.json';
import prairieBiomeData from '../data/biomes/prairie.json';
import coastalBiomeData from '../data/biomes/coastal.json';

// Map biome IDs to their class constructors
// This allows data-driven biome lookup while keeping rendering logic in classes
const biomeClassMap = {
    'desert': DesertBiome,
    'mountain': MountainBiome,
    'forest': ForestBiome,
    'prairie': PrairieBiome,
    'coastal': CoastalBiome
};

// Map biome IDs to their color palettes for seamless transitions
const biomeColorsMap = {
    'desert': DESERT_COLORS,
    'mountain': MOUNTAIN_COLORS,
    'forest': FOREST_COLORS,
    'prairie': PRAIRIE_COLORS,
    'coastal': COASTAL_COLORS
};

export class BiomeManager {
    constructor(scene, camera, worldTranslator, config = {}, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.worldTranslator = worldTranslator;
        this.config = config;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('biome', {
            dispose: () => this.dispose()
        });

        // Object pooling manager (optional)
        this.poolManager = null;

        // Whether to use the new BiomeRegistry (data-driven) or legacy biomeRegistry array
        this.useRegistry = config.useRegistry ?? true;

        // Load biome data into registry if using data-driven mode
        if (this.useRegistry) {
            this._loadBiomeData();
        }

        // Legacy biome registry - kept for backward compatibility
        // Each entry defines when a biome starts/ends and which class handles it
        this.biomeRegistryLegacy = [
            DESERT_BIOME_ENTRY,
            MOUNTAIN_BIOME_ENTRY,
        ];

        this.currentBiome = null;
        this.currentBiomeId = null;
        this.currentBiomeIndex = -1;  // Legacy - kept for compatibility
        this.totalMiles = 0;
    }

    /**
     * Load biome JSON data into the registry
     * @private
     */
    _loadBiomeData() {
        // Only load if registry is empty (avoid re-loading)
        if (biomeRegistry.size === 0) {
            biomeRegistry.loadFromJSON({
                biomes: [
                    desertBiomeData,
                    mountainBiomeData,
                    forestBiomeData,
                    prairieBiomeData,
                    coastalBiomeData
                ]
            });
        }
    }

    /**
     * Set the pool manager for object pooling.
     * @param {PoolManager} poolManager - The pool manager instance
     */
    setPoolManager(poolManager) {
        this.poolManager = poolManager;
    }

    /**
     * Set the color cycle manager for biome color notifications.
     * @param {ColorCycleManager} colorCycleManager - The color cycle manager instance
     */
    setColorCycleManager(colorCycleManager) {
        this.colorCycleManager = colorCycleManager;
    }

    async initialize(startingMiles = 0) {
        this.totalMiles = startingMiles;

        if (this.useRegistry) {
            // Data-driven mode: look up biome by mileage from registry
            // getBiomeForMiles now handles looping, so it should always return a biome
            const biomeData = biomeRegistry.getBiomeForMiles(this.totalMiles);
            if (biomeData) {
                await this.switchToBiomeById(biomeData.id);
            } else {
                // Fallback to first biome if no biomes defined at all
                const sorted = biomeRegistry.getAllSorted();
                if (sorted.length > 0) {
                    await this.switchToBiomeById(sorted[0].id);
                }
            }
        } else {
            // Legacy mode: use index-based lookup
            const biomeIndex = this.getBiomeIndexForMiles(this.totalMiles);
            await this.switchToBiome(biomeIndex);
        }

        this._onPreloadComplete?.();
    }

    /**
     * Get biome index for mileage (legacy mode)
     * @param {number} miles - Total miles traveled
     * @returns {number} Index into biomeRegistryLegacy
     */
    getBiomeIndexForMiles(miles) {
        // Find the biome that corresponds to the given mileage
        for (let i = 0; i < this.biomeRegistryLegacy.length; i++) {
            const biome = this.biomeRegistryLegacy[i];
            if (miles >= biome.minMiles && miles < biome.maxMiles) {
                return i;
            }
        }

        // If we're beyond all registered biomes, stay in the last one
        return this.biomeRegistryLegacy.length - 1;
    }

    /**
     * Switch to a biome by ID (data-driven mode)
     * Uses seamless transitions - updates config instead of recreating terrain.
     * New chunks will use new biome settings, existing chunks remain unchanged.
     *
     * @param {string} biomeId - The biome ID from registry
     */
    /**
     * Reset biome to initial state (used after prestige)
     * Forces a complete terrain regeneration, not a seamless transition.
     * @param {number} startingMiles - Miles to initialize at (usually 0)
     */
    async resetBiome(startingMiles = 0) {
        console.log(`[BiomeManager] Hard reset to miles: ${startingMiles}`);

        // Dispose current biome completely
        if (this.currentBiome) {
            this.currentBiome.dispose();
            this.currentBiome = null;
        }

        // Reset tracking state
        this.currentBiomeId = null;
        this.currentBiomeIndex = -1;
        this.totalMiles = startingMiles;

        // Reinitialize (will create fresh terrain)
        await this.initialize(startingMiles);
    }

    async switchToBiomeById(biomeId) {
        if (biomeId === this.currentBiomeId) {
            return; // Already in this biome
        }

        const biomeData = biomeRegistry.get(biomeId);
        const biomeColors = biomeColorsMap[biomeId];

        if (!biomeColors) {
            console.error(`[BiomeManager] No colors found for biome: ${biomeId}`);
            return;
        }

        const newBiomeName = biomeData.name;

        // Pre-warm object pools for the new biome
        if (this.poolManager) {
            this.poolManager.warmUpForBiome(newBiomeName);
        }

        // Get merged config from registry
        const registryConfig = biomeRegistry.getConfig(biomeId);
        const configWithBiomeName = { ...this.config, ...registryConfig, biomeName: newBiomeName };

        if (!this.currentBiome) {
            // First biome - create the BiomeRenderer instance
            // Use BiomeRenderer directly instead of biome subclass since we're doing seamless transitions
            this.currentBiome = new BiomeRenderer(this.scene, this.camera, this.worldTranslator, configWithBiomeName);

            // Set up color methods that BiomeRenderer expects
            this.currentBiome.biomeColors = biomeColors;
            this.currentBiome.palette.setBiomeColors(biomeColors);

            // Pass pool manager to the biome
            if (this.poolManager) {
                this.currentBiome.setPoolManager(this.poolManager);
            }

            // Create initial chunks to cover fog distance
            const chunkCount = this.config.chunkCount || 100;
            await this.currentBiome.initialize(chunkCount);

            console.log(`[BiomeManager] Initialized with biome: ${newBiomeName}`);
        } else {
            // Seamless transition - just update the config for new chunks
            this.currentBiome.updateBiomeConfig(configWithBiomeName, biomeColors);

            console.log(`[BiomeManager] Seamless transition to biome: ${newBiomeName}`);
        }

        this.currentBiomeId = biomeId;

        // Notify ColorCycleManager to update cached colors from new biome
        if (this.colorCycleManager) {
            this.colorCycleManager.onBiomeChanged();
        }
    }

    /**
     * Switch to a biome by index (legacy mode)
     * @param {number} biomeIndex - Index into biomeRegistryLegacy
     */
    async switchToBiome(biomeIndex) {
        if (biomeIndex === this.currentBiomeIndex) {
            return; // Already in this biome
        }

        const biomeInfo = this.biomeRegistryLegacy[biomeIndex];
        const newBiomeName = biomeInfo.name;

        // Pre-warm object pools for the new biome BEFORE disposing old
        if (this.poolManager) {
            this.poolManager.warmUpForBiome(newBiomeName);
        }

        // Dispose of old biome if it exists
        if (this.currentBiome) {
            this.currentBiome.dispose();
        }

        // Create new biome with biomeName in config
        const configWithBiomeName = { ...this.config, biomeName: newBiomeName };
        this.currentBiome = new biomeInfo.BiomeClass(this.scene, this.camera, this.worldTranslator, configWithBiomeName);

        // Pass pool manager to the new biome
        if (this.poolManager) {
            this.currentBiome.setPoolManager(this.poolManager);
        }

        // Create initial chunks to cover fog distance (reduced on mobile)
        const chunkCount = this.config.chunkCount || 100;
        await this.currentBiome.initialize(chunkCount);

        this.currentBiomeIndex = biomeIndex;
    }

    updateMiles(totalMiles) {
        this.totalMiles = totalMiles;

        if (this.useRegistry) {
            // Data-driven mode: look up biome by mileage from registry
            const biomeData = biomeRegistry.getBiomeForMiles(this.totalMiles);
            const targetBiomeId = biomeData?.id;

            // Debug: log biome lookup every 500 miles crossed
            const mileMarker = Math.floor(totalMiles / 500);
            if (!this._lastMileMarker || mileMarker !== this._lastMileMarker) {
                this._lastMileMarker = mileMarker;
                console.log(`[BiomeManager] Mile marker ${mileMarker * 500}: totalMiles=${totalMiles.toFixed(1)}, target=${targetBiomeId}, current=${this.currentBiomeId}`);
            }

            if (targetBiomeId && targetBiomeId !== this.currentBiomeId) {
                console.log(`[BiomeManager] Biome transition triggered at ${totalMiles} miles: ${this.currentBiomeId} -> ${targetBiomeId}`);
                this.switchToBiomeById(targetBiomeId);
            }
        } else {
            // Legacy mode: use index-based lookup
            const targetBiomeIndex = this.getBiomeIndexForMiles(this.totalMiles);
            if (targetBiomeIndex !== this.currentBiomeIndex) {
                this.switchToBiome(targetBiomeIndex);
            }
        }
    }

    update(deltaTime) {
        if (this.currentBiome) {
            this.currentBiome.update(deltaTime);
        }
    }

    setPalette(palette, timeIndex = 0) {
        if (this.currentBiome) {
            this.currentBiome.setPalette(palette, timeIndex);
        }
    }

    dispose() {
        if (this.currentBiome) {
            this.currentBiome.dispose();
            this.currentBiome = null;
        }
        // Note: poolManager is also disposed by main.js, so we just clear our reference
        this.poolManager = null;
    }

    // Expose current biome's road position method for camera following
    getRoadPositionAtZ(z) {
        if (this.currentBiome) {
            return this.currentBiome.getRoadPositionAtZ(z);
        }
        return {
            position: new THREE.Vector3(0, 0, z),
            tangent: new THREE.Vector3(0, 0, -1)
        };
    }
}
